import { buildCliLaunchInvocation, buildProviderExitError, classifyCliDriverTimeoutFailure, classifyRuntimePermissionDenialMessage, looksLikeRuntimePermissionDenialMessage, } from "@wakeloop/adapter-core";
import { CliDriverError, runCliDriver, } from "@wakeloop/adapter-core/drivers/cli-driver";
import { isLikelyClaudeContextLostError } from "../context/claude-context-heal.js";
import { parseClaudeOutputLine } from "../output/claude-output-parser.js";
import { resolveClaudeConversationPolicyOptions } from "./claude-conversation-policy.js";
import { resolveClaudePermissionMode, WAKELOOP_CLAUDE_ALLOWED_TOOLS, } from "./claude-permission-policy.js";
import { createClaudePreviewEmitter } from "./claude-preview-emitter.js";
export class ClaudeCliTransportError extends Error {
    code;
    errorType;
    providerAccessEvidence;
    constructor(input) {
        super(input.message);
        this.name = "ClaudeCliTransportError";
        this.code = input.code;
        this.errorType = input.errorType;
        if (input.providerAccessEvidence) {
            this.providerAccessEvidence = input.providerAccessEvidence;
        }
    }
}
export async function invokeClaudeCliTransport(input) {
    const prompt = normalizeOptionalText(input.prompt);
    if (!prompt) {
        throw new ClaudeCliTransportError({
            code: "schema.invalid",
            errorType: "config",
            message: "schema.invalid: prompt is required",
        });
    }
    const args = buildClaudeArgs({
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        conversationPolicy: input.conversationPolicy,
        executionMode: input.executionMode,
        prompt,
        sessionId: normalizeOptionalText(input.sessionId),
        streamingMode: input.streamingMode,
    });
    const invocation = buildCliLaunchInvocation({
        args,
        launchContract: input.launchContract,
    });
    const invokedAtMs = Date.now();
    let latestOutput = "";
    let latestResultIsError = false;
    let resolvedSessionId = normalizeOptionalText(input.sessionId);
    const providerErrorMessages = [];
    let firstStdoutAtMs = null;
    let firstOutputAtMs = null;
    const previewEmitter = createClaudePreviewEmitter({
        onPreview: input.onPreview,
        streamingMode: input.streamingMode,
    });
    let driverResult;
    try {
        driverResult = await runCliDriver({
            allowNonZeroExit: true,
            args: invocation.args,
            bin: invocation.bin,
            cwd: input.claudeWorkdir,
            env: input.env,
            onLifecycleEvent: input.onRuntimeEvent,
            onProcessControlEvent: input.onProcessControlEvent,
            onStderrLine: (event) => {
                collectProviderErrorMessage(event.line, providerErrorMessages);
            },
            onStdoutLine: (event) => {
                const nextState = handleClaudeStdoutLine({
                    eventLine: event.line,
                    firstOutputAtMs,
                    firstStdoutAtMs,
                    latestOutput,
                    latestResultIsError,
                    previewEmitter,
                    providerErrorMessages,
                    resolvedSessionId,
                });
                firstOutputAtMs = nextState.firstOutputAtMs;
                firstStdoutAtMs = nextState.firstStdoutAtMs;
                latestOutput = nextState.latestOutput;
                latestResultIsError = nextState.latestResultIsError;
                resolvedSessionId = nextState.resolvedSessionId;
                if (nextState.terminal) {
                    return {
                        complete: true,
                        terminateProcess: true,
                    };
                }
            },
            timeoutMs: input.claudeTimeoutMs,
        });
    }
    catch (error) {
        throw toClaudeCliTransportError(error, input.claudeTimeoutMs);
    }
    await previewEmitter.flush();
    if (driverResult.exitCode !== 0 || latestResultIsError) {
        const classifiedFailure = classifyClaudeFailure({
            providerErrorMessages,
            stderrText: driverResult.stderrText,
            stdoutText: driverResult.stdoutText,
        });
        throw new ClaudeCliTransportError({
            code: classifiedFailure.code,
            errorType: classifiedFailure.errorType,
            message: classifiedFailure.message,
            ...(classifiedFailure.providerAccessEvidence
                ? { providerAccessEvidence: classifiedFailure.providerAccessEvidence }
                : {}),
        });
    }
    const output = normalizeOptionalText(latestOutput);
    if (!output) {
        throw new ClaudeCliTransportError({
            code: "runtime.output.empty",
            errorType: "adapter",
            message: "runtime.output.empty: claude finished without agent output",
        });
    }
    return {
        output,
        sessionId: normalizeOptionalText(resolvedSessionId),
        telemetry: {
            completedAtMs: Date.now(),
            ...(driverResult.lifecycle.exitCode === null
                ? {}
                : { exitCode: driverResult.lifecycle.exitCode }),
            ...(driverResult.lifecycle.exitObservedAtMs === null
                ? {}
                : { exitObservedAtMs: driverResult.lifecycle.exitObservedAtMs }),
            ...(driverResult.lifecycle.exitSignal === null
                ? {}
                : { exitSignal: driverResult.lifecycle.exitSignal }),
            ...(firstOutputAtMs === null ? {} : { firstOutputAtMs }),
            ...(driverResult.lifecycle.firstStderrAtMs === null
                ? {}
                : { firstStderrAtMs: driverResult.lifecycle.firstStderrAtMs }),
            ...(firstStdoutAtMs === null ? {} : { firstStdoutAtMs }),
            invokedAtMs,
            previewEmitted: previewEmitter.emitted,
            ...(driverResult.lifecycle.processId === null
                ? {}
                : { processId: driverResult.lifecycle.processId }),
            ...(driverResult.lifecycle.processSpawnedAtMs === null
                ? {}
                : { processSpawnedAtMs: driverResult.lifecycle.processSpawnedAtMs }),
            resumedContext: normalizeOptionalText(input.sessionId) !== null,
            ...(driverResult.lifecycle.stdinClosedAtMs === null
                ? {}
                : { stdinClosedAtMs: driverResult.lifecycle.stdinClosedAtMs }),
            streamingMode: input.streamingMode ?? "final",
            ...(driverResult.lifecycle.timeoutTriggeredAtMs === null
                ? {}
                : {
                    timeoutTriggeredAtMs: driverResult.lifecycle.timeoutTriggeredAtMs,
                }),
        },
    };
}
export const buildClaudeArgs = (input) => {
    const args = [];
    if (input.sessionId) {
        args.push("--resume", input.sessionId);
    }
    appendClaudeConversationPolicyArgs(args, input.conversationPolicy);
    args.push("-p", input.prompt);
    appendClaudeAgentControllerPermissionModeArgs(args, {
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        executionMode: input.executionMode,
    });
    if (input.streamingMode === "stream") {
        args.push("--output-format", "stream-json", "--include-partial-messages", "--verbose");
        return args;
    }
    args.push("--output-format", "json");
    return args;
};
const appendClaudeAgentControllerPermissionModeArgs = (args, input) => {
    args.push("--allowedTools", ...WAKELOOP_CLAUDE_ALLOWED_TOOLS);
    args.push("--permission-mode", resolveClaudePermissionMode(input));
};
const appendClaudeConversationPolicyArgs = (args, conversationPolicy) => {
    const conversationPolicyOptions = resolveClaudeConversationPolicyOptions(conversationPolicy);
    const model = conversationPolicyOptions.model;
    if (model) {
        args.push("--model", model);
    }
    const fallbackModel = conversationPolicyOptions.fallbackModel;
    if (fallbackModel) {
        args.push("--fallback-model", fallbackModel);
    }
    const reasoningLevel = conversationPolicyOptions.reasoningLevel;
    if (reasoningLevel) {
        args.push("--effort", reasoningLevel);
    }
};
const handleClaudeStdoutLine = (input) => {
    let firstStdoutAtMs = input.firstStdoutAtMs;
    let firstOutputAtMs = input.firstOutputAtMs;
    let latestOutput = input.latestOutput;
    let latestResultIsError = input.latestResultIsError;
    let resolvedSessionId = input.resolvedSessionId;
    if (firstStdoutAtMs === null) {
        firstStdoutAtMs = Date.now();
    }
    const parsedLine = parseClaudeOutputLine(input.eventLine);
    if (!parsedLine) {
        return {
            firstOutputAtMs,
            firstStdoutAtMs,
            latestOutput,
            latestResultIsError,
            resolvedSessionId,
            terminal: false,
        };
    }
    if (parsedLine.previewDeltaText) {
        const nextPreviewText = input.previewEmitter.append(parsedLine.previewDeltaText);
        if (nextPreviewText && firstOutputAtMs === null) {
            firstOutputAtMs = Date.now();
        }
    }
    if (parsedLine.resultText) {
        latestOutput = parsedLine.resultText;
        if (firstOutputAtMs === null) {
            firstOutputAtMs = Date.now();
        }
    }
    if (parsedLine.errorMessage) {
        input.providerErrorMessages.push(parsedLine.errorMessage);
    }
    if (parsedLine.isErrorResult === true) {
        latestResultIsError = true;
    }
    else {
        latestResultIsError = false;
        if (parsedLine.sessionId) {
            resolvedSessionId = parsedLine.sessionId;
        }
    }
    return {
        firstOutputAtMs,
        firstStdoutAtMs,
        latestOutput,
        latestResultIsError,
        resolvedSessionId,
        terminal: parsedLine.terminal,
    };
};
const toClaudeCliTransportError = (error, timeoutMs) => {
    if (error instanceof ClaudeCliTransportError) {
        return error;
    }
    if (error instanceof CliDriverError) {
        if (error.code === "process.timeout") {
            const classifiedFailure = classifyCliDriverTimeoutFailure({
                error,
                providerLabel: "claude",
                timeoutMs,
            });
            return new ClaudeCliTransportError(classifiedFailure);
        }
        if (error.code === "process.spawn_failed") {
            return new ClaudeCliTransportError({
                code: "upstream_unreachable",
                errorType: "adapter",
                message: `upstream_unreachable: failed to spawn claude (${error.message})`,
            });
        }
        if (error.code === "process.non_zero_exit") {
            const classifiedFailure = classifyClaudeFailure({
                providerErrorMessages: [],
                stderrText: error.stderrText,
                stdoutText: error.stdoutText,
            });
            return new ClaudeCliTransportError({
                code: classifiedFailure.code,
                errorType: classifiedFailure.errorType,
                message: classifiedFailure.message,
                ...(classifiedFailure.providerAccessEvidence
                    ? { providerAccessEvidence: classifiedFailure.providerAccessEvidence }
                    : {}),
            });
        }
    }
    const fallbackMessage = error instanceof Error ? error.message : String(error);
    return new ClaudeCliTransportError({
        code: "upstream_unreachable",
        errorType: "adapter",
        message: `upstream_unreachable: failed to execute claude (${fallbackMessage})`,
    });
};
export const classifyClaudeFailure = (input) => {
    const providerMessage = resolveProviderFailureMessage(input);
    if (providerMessage) {
        const lowered = providerMessage.toLowerCase();
        const providerAccessEvidence = buildClaudeCodeProviderAccessEvidence(lowered);
        const permissionDenial = classifyRuntimePermissionDenialMessage({
            message: providerMessage,
            source: "claude-code",
        });
        if (permissionDenial) {
            return {
                code: permissionDenial.code,
                errorType: permissionDenial.errorType,
                message: permissionDenial.message,
            };
        }
        if (isAuthFailure(lowered)) {
            return {
                code: "auth.required",
                errorType: "auth",
                message: providerMessage,
                ...(providerAccessEvidence ? { providerAccessEvidence } : {}),
            };
        }
        if (isQuotaFailure(lowered)) {
            return {
                code: "quota.exceeded",
                errorType: "adapter",
                message: providerMessage,
                ...(providerAccessEvidence ? { providerAccessEvidence } : {}),
            };
        }
        if (isRateLimitFailure(lowered)) {
            return {
                code: "rate_limited",
                errorType: "adapter",
                message: providerMessage,
            };
        }
        if (isModelConfigFailure(lowered)) {
            return {
                code: "config.invalid",
                errorType: "config",
                message: toConfigInvalidMessage(providerMessage),
            };
        }
        if (isLikelyClaudeContextLostError(providerMessage)) {
            return {
                code: "context.lost",
                errorType: "adapter",
                message: providerMessage,
            };
        }
    }
    const stderrText = normalizeOptionalText(input.stderrText);
    if (stderrText) {
        return {
            code: "upstream_unreachable",
            errorType: "adapter",
            message: `upstream_unreachable: ${stderrText}`,
        };
    }
    return {
        ...buildProviderExitError({
            providerLabel: "claude",
        }),
    };
};
const resolveProviderFailureMessage = (input) => {
    const candidateMessages = [
        ...input.providerErrorMessages,
        ...extractErrorMessagesFromStdout(input.stdoutText),
        ...(normalizeOptionalText(input.stderrText)
            ? [normalizeOptionalText(input.stderrText)]
            : []),
    ].filter((value) => Boolean(value));
    for (const candidate of candidateMessages) {
        if (looksLikeProviderFailure(candidate)) {
            return candidate;
        }
    }
    return candidateMessages[0] ?? null;
};
const extractErrorMessagesFromStdout = (value) => {
    const stdoutText = normalizeOptionalText(value);
    if (!stdoutText) {
        return [];
    }
    const messages = [];
    const lines = stdoutText.split("\n");
    for (const line of lines) {
        const parsedLine = parseClaudeOutputLine(line);
        if (parsedLine?.errorMessage) {
            messages.push(parsedLine.errorMessage);
        }
    }
    return messages;
};
const collectProviderErrorMessage = (line, messages) => {
    const normalizedLine = normalizeOptionalText(line);
    if (!normalizedLine) {
        return;
    }
    messages.push(normalizedLine);
};
const looksLikeProviderFailure = (value) => {
    const normalized = value.toLowerCase();
    return (looksLikeRuntimePermissionDenialMessage(value) ||
        isAuthFailure(normalized) ||
        isQuotaFailure(normalized) ||
        isRateLimitFailure(normalized) ||
        isModelConfigFailure(normalized) ||
        isLikelyClaudeContextLostError(normalized));
};
const isAuthFailure = (value) => {
    return (value.includes("not logged in") ||
        value.includes("please run /login") ||
        value.includes("authentication_failed") ||
        value.includes("unauthorized") ||
        value.includes("401") ||
        isClaudeCodeAccountAccessFailure(value));
};
const isQuotaFailure = (value) => {
    return (value.includes("insufficient_quota") ||
        value.includes("insufficient balance") ||
        value.includes("insufficient_balance") ||
        value.includes("quota exceeded") ||
        value.includes("exceeded your current quota") ||
        value.includes("not enough credits") ||
        value.includes("out of credits") ||
        value.includes("credits exhausted") ||
        value.includes("balance is too low") ||
        value.includes("payment required") ||
        value.includes("billing") ||
        isClaudeCodeSubscriptionAccessFailure(value));
};
const isModelConfigFailure = (value) => {
    return (value.includes("model_not_found") ||
        value.includes("unknown model") ||
        value.includes("invalid model") ||
        value.includes("requested model") ||
        value.includes("unsupported model") ||
        (value.includes("model") &&
            (value.includes("does not exist") ||
                value.includes("no longer available") ||
                value.includes("retired") ||
                value.includes("deprecated"))) ||
        (value.includes("invalid_request_error") && value.includes("model")) ||
        value.includes('"param": "model"'));
};
const toConfigInvalidMessage = (value) => {
    return value.toLowerCase().startsWith("config.invalid:")
        ? value
        : `config.invalid: ${value}`;
};
const isClaudeCodeAccountAccessFailure = (value) => {
    return ((value.includes("organization") &&
        value.includes("disabled") &&
        value.includes("access")) ||
        (value.includes("claude code") &&
            (value.includes("access is disabled") ||
                value.includes("disabled claude code") ||
                value.includes("access unavailable") ||
                value.includes("not enabled"))));
};
const isClaudeCodeSubscriptionAccessFailure = (value) => {
    return (value.includes("subscription") &&
        (value.includes("disabled") ||
            value.includes("inactive") ||
            value.includes("required") ||
            value.includes("unavailable")));
};
const buildClaudeCodeProviderAccessEvidence = (normalizedProviderMessage) => {
    if (isClaudeCodeAccountAccessFailure(normalizedProviderMessage)) {
        return {
            observedAtMs: Date.now(),
            provider: "claude-code",
            reasonCode: "provider_account.organization_access_disabled",
            source: "wake_failure",
        };
    }
    if (isClaudeCodeSubscriptionAccessFailure(normalizedProviderMessage)) {
        return {
            observedAtMs: Date.now(),
            provider: "claude-code",
            reasonCode: "provider_account.subscription_access_unavailable",
            source: "wake_failure",
        };
    }
    return null;
};
const isRateLimitFailure = (value) => {
    return (value.includes("429") ||
        value.includes("rate limit") ||
        value.includes("too many requests"));
};
function normalizeText(value) {
    return String(value ?? "").trim();
}
function normalizeOptionalText(value) {
    const normalized = normalizeText(value);
    return normalized.length > 0 ? normalized : null;
}
